<template>
  <transition name="mg-fade">
    <div
      v-if="current"
      class="mg"
      role="dialog"
      aria-modal="true"
      @click.self="$emit('close')"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <header class="mg__bar">
        <span class="mg__count">{{ index + 1 }} / {{ items.length }}</span>
        <span class="mg__name" :title="current.name">{{ current.name }}</span>
        <a class="mg__btn" :href="current.url" target="_blank" rel="noopener" title="Открыть оригинал в новой вкладке" @click.stop>
          <i class="fas fa-external-link-alt"></i>
        </a>
        <button type="button" class="mg__btn" aria-label="Закрыть" title="Закрыть (Esc)" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <button v-if="items.length > 1" type="button" class="mg__nav mg__nav--prev" aria-label="Предыдущее" @click.stop="step(-1)">
        <i class="fas fa-chevron-left"></i>
      </button>

      <div class="mg__stage" @click.self="$emit('close')">
        <img v-if="current.type === 'image'" :key="current.id" class="mg__media" :src="current.url" :alt="current.name">
        <video v-else :key="current.id" class="mg__media" :src="current.url" controls autoplay playsinline></video>
      </div>

      <button v-if="items.length > 1" type="button" class="mg__nav mg__nav--next" aria-label="Следующее" @click.stop="step(1)">
        <i class="fas fa-chevron-right"></i>
      </button>

      <!-- Ленты миниатюр для перехода к нужному снимку -->
      <div v-if="items.length > 1" class="mg__thumbs" @click.stop>
        <button
          v-for="(item, i) in items"
          :key="item.id"
          type="button"
          class="mg__thumb"
          :class="{ 'is-active': i === index }"
          :aria-label="'Показать ' + (i + 1)"
          @click="$emit('update:index', i)"
        >
          <img v-if="item.type === 'image'" :src="item.url" :alt="item.name" loading="lazy">
          <i v-else class="fas fa-play"></i>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
// Просмотр фото и видео поверх страницы: листание стрелками, клавишами ← →, свайпом; Esc и клик по фону закрывают.
// Использование: <media-gallery :items="[{ id, type: 'image'|'video', name, url }]" :index.sync="i" @close="i = null" />
// index = null — галерея закрыта.
export default {
  name: 'MediaGallery',
  props: {
    items: { type: Array, default: () => [] },
    index: { type: Number, default: null }
  },
  data() {
    return { touchX: null }
  },
  computed: {
    current() {
      return this.index !== null && this.items[this.index] ? this.items[this.index] : null
    }
  },
  watch: {
    current(value) {
      // Перехватываем клавиши раньше окна под галереей: Esc закрывает только галерею
      if (value) window.addEventListener('keydown', this.onKey, true)
      else window.removeEventListener('keydown', this.onKey, true)
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKey, true)
  },
  methods: {
    step(delta) {
      const n = this.items.length
      if (n < 2) return
      this.$emit('update:index', (this.index + delta + n) % n)
    },
    onKey(e) {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        this.$emit('close')
      } else if (e.key === 'ArrowLeft') this.step(-1)
      else if (e.key === 'ArrowRight') this.step(1)
    },
    onTouchStart(e) {
      this.touchX = e.changedTouches[0].clientX
    },
    // Свайп влево — следующее, вправо — предыдущее (не мешает управлению видео: нужен заметный сдвиг).
    onTouchEnd(e) {
      if (this.touchX === null) return
      const dx = e.changedTouches[0].clientX - this.touchX
      this.touchX = null
      if (Math.abs(dx) > 60) this.step(dx < 0 ? 1 : -1)
    }
  }
}
</script>

<style scoped>
.mg {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 12, 20, 0.94);
  color: #fff;
}

.mg__bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: calc(var(--space-6) + env(safe-area-inset-top, 0px)) var(--space-10) var(--space-6);
  background: linear-gradient(rgba(0, 0, 0, 0.55), transparent);
}
.mg__count { font-size: var(--text-sm); opacity: 0.85; flex-shrink: 0; }
.mg__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-sm);
  opacity: 0.85;
}
.mg__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: var(--text-md);
  cursor: pointer;
  text-decoration: none;
}
.mg__btn:hover { background: rgba(255, 255, 255, 0.28); }

.mg__stage {
  position: absolute;
  inset: 3.5rem 0 5.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3.5rem;
}
.mg__media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: var(--radius-md);
  background: #000;
}

.mg__nav {
  position: absolute;
  top: 50%;
  z-index: 2;
  transform: translateY(-50%);
  width: 3rem;
  height: 3rem;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: var(--text-md);
  cursor: pointer;
}
.mg__nav:hover { background: rgba(255, 255, 255, 0.28); }
.mg__nav--prev { left: var(--space-8); }
.mg__nav--next { right: var(--space-8); }

.mg__thumbs {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  gap: var(--space-5);
  padding: var(--space-6) var(--space-10) calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
  overflow-x: auto;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));
}
.mg__thumb {
  flex: 0 0 auto;
  width: 3.5rem;
  height: 3.5rem;
  padding: 0;
  overflow: hidden;
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  opacity: 0.65;
}
.mg__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.mg__thumb.is-active { border-color: var(--primary); opacity: 1; }

.mg-fade-enter-active,
.mg-fade-leave-active { transition: opacity var(--duration-normal) var(--ease-base); }
.mg-fade-enter,
.mg-fade-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .mg__stage { padding: 0 var(--space-4); inset: 3.5rem 0 5rem; }
  /* На телефоне листают свайпом; стрелки меньше и прижаты к краям */
  .mg__nav { width: 2.5rem; height: 2.5rem; }
  .mg__nav--prev { left: var(--space-4); }
  .mg__nav--next { right: var(--space-4); }
}
</style>
